import { useEffect, useState, useRef } from 'react';
import { HiSelector, HiOfficeBuilding, HiX } from '../../components/icons';
import { useSelectedTenant, type SelectedTenant } from '../../stores/tenantStore';
import { tenantService, type Tenant } from '../../services/tenant.service';

export function TenantSelector() {
  const { selectedTenant, setSelectedTenant } = useSelectedTenant();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadTenants = async () => {
      setIsLoading(true);
      try {
        const allTenants = await tenantService.getAllTenants();
        setTenants(allTenants);
      } catch {
        // Silently fail - tenants will be empty
      } finally {
        setIsLoading(false);
      }
    };
    loadTenants();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTenants = tenants.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.id.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (tenant: Tenant) => {
    const selected: SelectedTenant = { id: tenant.id, name: tenant.name };
    setSelectedTenant(selected);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTenant(null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
          border focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-1
          ${selectedTenant
            ? 'bg-xtnd-blue/10 border-xtnd-blue text-xtnd-dark-800 dark:text-xtnd-light-200'
            : 'bg-white dark:bg-xtnd-dark-800 border-xtnd-dark-200 dark:border-xtnd-dark-600 text-xtnd-dark-500 dark:text-xtnd-dark-300'
          }
        `}
        aria-label="Select tenant"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <HiOfficeBuilding className="h-4 w-4 flex-shrink-0" />
        <span className="max-w-[160px] truncate">
          {selectedTenant ? selectedTenant.name : 'Select Tenant'}
        </span>
        {selectedTenant ? (
          <button
            type="button"
            onClick={handleClear}
            className="ml-1 p-0.5 rounded hover:bg-xtnd-dark-200 dark:hover:bg-xtnd-dark-600 transition-colors"
            aria-label="Clear tenant selection"
          >
            <HiX className="h-3 w-3" />
          </button>
        ) : (
          <HiSelector className="h-4 w-4 flex-shrink-0" />
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-xtnd-dark-800 border border-xtnd-dark-200 dark:border-xtnd-dark-600 rounded-lg shadow-lg z-50 overflow-hidden"
          role="listbox"
          aria-label="Tenant list"
        >
          <div className="p-2 border-b border-xtnd-dark-100 dark:border-xtnd-dark-700">
            <input
              type="text"
              placeholder="Search tenants..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-900 text-xtnd-dark-800 dark:text-white placeholder-xtnd-dark-400 dark:placeholder-xtnd-dark-400 focus:outline-none focus:ring-2 focus:ring-xtnd-blue"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              aria-label="Search tenants"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
                Loading tenants...
              </div>
            ) : filteredTenants.length === 0 ? (
              <div className="px-4 py-3 text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
                No tenants found
              </div>
            ) : (
              filteredTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  type="button"
                  onClick={() => handleSelect(tenant)}
                  role="option"
                  aria-selected={selectedTenant?.id === tenant.id}
                  className={`
                    w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between
                    ${selectedTenant?.id === tenant.id
                      ? 'bg-xtnd-blue/10 text-xtnd-dark-800 dark:text-white font-medium'
                      : 'text-xtnd-dark-700 dark:text-xtnd-dark-200 hover:bg-xtnd-light-100 dark:hover:bg-xtnd-dark-700'
                    }
                  `}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{tenant.name}</div>
                    <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-400 truncate">
                      {tenant.id}
                    </div>
                  </div>
                  <span
                    className={`
                      ml-2 flex-shrink-0 inline-block w-2 h-2 rounded-full
                      ${tenant.clioConnectionStatus === 'connected' ? 'bg-green-500' : tenant.clioConnectionStatus === 'expired' ? 'bg-yellow-500' : 'bg-gray-400'}
                    `}
                    title={`Clio: ${tenant.clioConnectionStatus}`}
                  />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
